import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { CalendarOff, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const getErrorMessage = (e, fallback) => {
  const detail = e.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const emptyForm = () => ({ date: '', name: '', description: '' });

export const HolidaysTab = ({ api }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get('/holidays');
      setList(res.data || []);
    } catch (e) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return list.filter(h => h.date >= today);
  }, [list]);
  const past = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return list.filter(h => h.date < today);
  }, [list]);

  const openCreate = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const save = async () => {
    if (!form.date) { toast.error('Pick a date'); return; }
    if (!form.name.trim()) { toast.error('Holiday name is required'); return; }
    setSaving(true);
    try {
      await api.post('/holidays', {
        date: form.date,
        name: form.name.trim(),
        description: form.description || null,
      });
      toast.success('Holiday added — bookings are now blocked on this date across all centres');
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add holiday'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Remove holiday on ${h.date} (${h.name})? Bookings will become allowed on this date again.`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success('Holiday removed');
      fetchAll();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to remove holiday'));
    }
  };

  return (
    <div className="space-y-6" data-testid="holidays-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-primary" /> Holiday Management
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Mark dates the platform is closed. Bookings, guest bookings and staff-assisted bookings are blocked on these dates across every centre.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="holidays-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
            <Button size="sm" onClick={openCreate} data-testid="holidays-new-btn"><Plus className="w-4 h-4 mr-1" /> Mark Holiday</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-sm text-muted-foreground py-8">Loading…</p>
          ) : list.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">No holidays marked yet. Click "Mark Holiday" to close the platform on a specific date.</p>
          ) : (
            <div className="space-y-6">
              {upcoming.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Upcoming</h4>
                  <HolidayTable rows={upcoming} onRemove={remove} />
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Past</h4>
                  <HolidayTable rows={past} onRemove={remove} />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md" data-testid="holidays-form-modal">
          <DialogHeader>
            <DialogTitle>Mark Holiday</DialogTitle>
            <DialogDescription>All bookable types at all centres will be closed for booking on this date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={form.date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="holiday-form-date"
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Diwali, Independence Day"
                data-testid="holiday-form-name"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Internal note, not shown to customers"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="holiday-form-save">{saving ? 'Saving…' : 'Mark Holiday'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const HolidayTable = ({ rows, onRemove }) => (
  <div className="overflow-x-auto rounded-lg border">
    <table className="w-full text-sm">
      <thead className="bg-muted/40">
        <tr className="text-left text-xs uppercase text-muted-foreground">
          <th className="px-3 py-2">Date</th>
          <th className="px-3 py-2">Name</th>
          <th className="px-3 py-2">Notes</th>
          <th className="px-3 py-2">Added By</th>
          <th className="px-3 py-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(h => (
          <tr key={h.id} className="border-t hover:bg-muted/20" data-testid={`holiday-row-${h.date}`}>
            <td className="px-3 py-2 font-mono">{new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</td>
            <td className="px-3 py-2 font-semibold">{h.name}</td>
            <td className="px-3 py-2 text-xs text-muted-foreground">{h.description || '—'}</td>
            <td className="px-3 py-2 text-xs text-muted-foreground">{h.created_by_name || '—'}</td>
            <td className="px-3 py-2">
              <Button variant="ghost" size="sm" onClick={() => onRemove(h)} title="Remove" className="text-red-500" data-testid={`holiday-delete-${h.date}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default HolidaysTab;
